// Pure helpers for the publisher UI — the app's only unit-tested logic (the rest is DOM/OAuth glue).
import type { FileInput } from "@szzt/core";
import type { FlowState } from "@szzt/frontend";

/**
 * Derive a non-empty site name from the dropped files (core `build()` requires `meta.name`). Prefers
 * an `index.html`'s stem, else the first file's stem, else a stable fallback.
 */
export function deriveSiteName(files: readonly FileInput[]): string {
  const index = files.find((f) => /(^|\/)index\.html?$/i.test(f.path));
  const pick = index ?? files[0];
  if (pick === undefined) return "site";
  const base = pick.path.split("/").pop() ?? "";
  const stem = base.replace(/\.[^.]+$/, "").trim();
  return stem === "" ? "site" : stem;
}

const NAMED_ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

/** Decode the handful of HTML entities that realistically appear in a `<title>` (named + numeric). */
function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (whole, code: string) => {
    const key = code.toLowerCase();
    if (key in NAMED_ENTITIES) return NAMED_ENTITIES[key] ?? whole;
    try {
      if (key.startsWith("#x")) return String.fromCodePoint(parseInt(key.slice(2), 16));
      if (key.startsWith("#")) return String.fromCodePoint(parseInt(key.slice(1), 10));
    } catch {
      return whole; // out-of-range code point → leave the entity as written
    }
    return whole;
  });
}

/**
 * Extract the `<title>` text from an HTML document's bytes, or undefined if there is none. Whitespace is
 * collapsed and the common entities decoded. Deliberately a regex, not a DOM parse — this runs on raw
 * dropped bytes before any build, and a title is a plain text node.
 */
export function titleFromHtml(bytes: Uint8Array): string | undefined {
  let html: string;
  try {
    html = new TextDecoder().decode(bytes);
  } catch {
    return undefined;
  }
  const m = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html);
  if (m === null) return undefined;
  const text = decodeEntities(m[1] ?? "").replace(/\s+/g, " ").trim();
  return text.length > 0 ? text : undefined;
}

/**
 * The initial site name to prefill the (editable) name field with: the index.html `<title>` if present,
 * else the file/folder/zip stem (`deriveSiteName`). When neither yields anything meaningful (a directory
 * listing or images-only drop with no title) the user edits the field to name it.
 */
export function deriveInitialName(files: readonly FileInput[]): string {
  const index = files.find((f) => /(^|\/)index\.html?$/i.test(f.path));
  if (index !== undefined) {
    const title = titleFromHtml(index.bytes);
    if (title !== undefined) return title;
  }
  return deriveSiteName(files);
}

// Unauthenticated public-appview endpoint for handle typeahead (app.bsky.actor.searchActorsTypeahead).
// The suggestions are only a convenience for the login field; a full PDS URL or DID bypasses them.
const TYPEAHEAD_ENDPOINT = "https://public.api.bsky.app/xrpc/app.bsky.actor.searchActorsTypeahead";

/** Build the unauthenticated typeahead query URL for a partial handle. */
export function typeaheadUrl(query: string, limit = 8): string {
  const u = new URL(TYPEAHEAD_ENDPOINT);
  u.searchParams.set("q", query);
  u.searchParams.set("limit", String(limit));
  return u.href;
}

/** One handle suggestion: the atproto handle, a friendly display label, and (best-effort) an avatar URL. */
export interface HandleSuggestion {
  handle: string;
  label: string;
  avatar?: string;
}

/**
 * Extract handle suggestions from a `searchActorsTypeahead` response body. Defensive by design — the
 * body is untrusted network JSON, so any non-conforming shape yields an empty list rather than throwing.
 * Label prefers a non-empty `displayName`, else the handle itself; `avatar` is included only when the
 * actor carries a non-empty avatar URL (the dropdown shows a placeholder circle otherwise).
 */
export function actorSuggestions(body: unknown): HandleSuggestion[] {
  if (body === null || typeof body !== "object" || !("actors" in body)) return [];
  const actors = (body as { actors: unknown }).actors;
  if (!Array.isArray(actors)) return [];
  const out: HandleSuggestion[] = [];
  for (const a of actors) {
    if (a === null || typeof a !== "object") continue;
    const handle = (a as { handle?: unknown }).handle;
    if (typeof handle !== "string" || handle.length === 0) continue;
    const displayName = (a as { displayName?: unknown }).displayName;
    const label =
      typeof displayName === "string" && displayName.trim().length > 0 ? displayName.trim() : handle;
    const suggestion: HandleSuggestion = { handle, label };
    const avatar = (a as { avatar?: unknown }).avatar;
    if (typeof avatar === "string" && avatar.length > 0) suggestion.avatar = avatar;
    out.push(suggestion);
  }
  return out;
}

/**
 * A localStorage hint that a session likely exists. NOT an auth token — the real OAuth session lives in
 * the OAuth client's own storage; this only lets the UI render an optimistic "restoring session…" shell
 * on load instead of flashing the signed-out form while the (async) OAuth restore runs.
 */
export interface SessionHint {
  did: string;
  handle?: string;
  avatar?: string;
}

/** Serialize a session hint for localStorage. Omits absent fields (kept out, not set to null). */
export function serializeSessionHint(did: string, handle?: string, avatar?: string): string {
  const hint: SessionHint = { did };
  if (handle !== undefined && handle.length > 0) hint.handle = handle;
  if (avatar !== undefined && avatar.length > 0) hint.avatar = avatar;
  return JSON.stringify(hint);
}

/**
 * Parse a session hint from a raw localStorage value. Defensive by design — the value is untrusted
 * persisted JSON (could be malformed, stale, or from another app version), so any non-conforming input
 * yields undefined rather than throwing (same discipline as `actorSuggestions`). A hint needs a
 * non-empty `did`; `handle`/`avatar` are included only when non-empty strings.
 */
export function parseSessionHint(raw: string | null): SessionHint | undefined {
  if (raw === null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== "object") return undefined;
  const did = (parsed as { did?: unknown }).did;
  if (typeof did !== "string" || did.length === 0) return undefined;
  const hint: SessionHint = { did };
  const handle = (parsed as { handle?: unknown }).handle;
  if (typeof handle === "string" && handle.length > 0) hint.handle = handle;
  const avatar = (parsed as { avatar?: unknown }).avatar;
  if (typeof avatar === "string" && avatar.length > 0) hint.avatar = avatar;
  return hint;
}

/**
 * Device-local config for an optional external RASL mirror the publisher pushes each site's blobs to
 * (BYO CDN, szzt-spec.md §7.3 / §13.1). Held in localStorage, NOT tied to an account — it's a
 * publishing setting for this browser. `host` is the bucket's public serve domain (advertised bare-host
 * in the site record's `hints`); the other four are Cloudflare R2 S3-API credentials used to sign the
 * in-browser upload. All five are required for a usable mirror (a partial config is treated as absent).
 */
export interface R2Config {
  accountId: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
  host: string;
}

/**
 * Normalize a user-entered mirror host to the bare-host form the gateway expects in `hints` (§13.1):
 * strip any `https://`/`http://` scheme, any path/query, and surrounding whitespace (e.g.
 * `https://cdn.example.com/` → `cdn.example.com`). The gateway builds `https://<host>/.well-known/rasl/…`
 * from this, so a scheme or trailing slash left in would produce a broken URL.
 */
export function normalizeMirrorHost(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "");
}

/** Serialize an {@link R2Config} for localStorage (all fields present by construction). */
export function serializeR2Config(cfg: R2Config): string {
  return JSON.stringify(cfg);
}

/**
 * Parse an {@link R2Config} from a raw localStorage value. Defensive by design (untrusted persisted
 * JSON, same discipline as {@link parseSessionHint}): every field must be a non-empty string, else the
 * whole config reads as absent (undefined) so a half-written/stale value never drives a broken push.
 */
export function parseR2Config(raw: string | null): R2Config | undefined {
  if (raw === null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== "object") return undefined;
  const p = parsed as Record<string, unknown>;
  const fields = ["accountId", "bucket", "accessKeyId", "secretAccessKey", "host"] as const;
  const out = {} as Record<(typeof fields)[number], string>;
  for (const f of fields) {
    const v = p[f];
    if (typeof v !== "string" || v.length === 0) return undefined;
    out[f] = v;
  }
  return out;
}

/**
 * Device-local config for an optional external IPFS mirror the publisher pushes each site's blocks to
 * (BYO IPFS, szzt-spec.md §7.3 / §13.1). Held in localStorage, NOT tied to an account — a
 * publishing setting for this browser. `apiUrl` is the IPFS node's `block/put` write API the blocks
 * are pushed to (a full URL with scheme/port, e.g. `https://ipfs.example.com:5001` — any node speaking
 * the Kubo-style `block/put` RPC, i.e. Kubo itself or an IPFS Cluster proxy); `gatewayHost` is the
 * PUBLIC gateway host that serves them (advertised `ipfs:`-prefixed in the site record's `hints`, so
 * it's normalized to a bare host). `authorization` is an optional header for a protected API
 * (`Basic …`/`Bearer …`).
 * `apiUrl` and `gatewayHost` are required for a usable mirror; a partial config is treated as absent.
 */
export interface IpfsConfig {
  apiUrl: string;
  gatewayHost: string;
  authorization?: string;
}

/** Serialize an {@link IpfsConfig} for localStorage. Omits an absent/empty `authorization` key. */
export function serializeIpfsConfig(cfg: IpfsConfig): string {
  const out: IpfsConfig = { apiUrl: cfg.apiUrl, gatewayHost: cfg.gatewayHost };
  if (cfg.authorization !== undefined && cfg.authorization.length > 0) out.authorization = cfg.authorization;
  return JSON.stringify(out);
}

/**
 * Parse an {@link IpfsConfig} from a raw localStorage value. Defensive by design (untrusted persisted
 * JSON, same discipline as {@link parseR2Config}): `apiUrl` and `gatewayHost` must both be non-empty
 * strings, else the whole config reads as absent (undefined) so a half-written/stale value never
 * drives a broken push. `authorization` is included only when present as a non-empty string.
 */
export function parseIpfsConfig(raw: string | null): IpfsConfig | undefined {
  if (raw === null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== "object") return undefined;
  const p = parsed as Record<string, unknown>;
  const apiUrl = p["apiUrl"];
  const gatewayHost = p["gatewayHost"];
  if (typeof apiUrl !== "string" || apiUrl.length === 0) return undefined;
  if (typeof gatewayHost !== "string" || gatewayHost.length === 0) return undefined;
  const out: IpfsConfig = { apiUrl, gatewayHost };
  const authorization = p["authorization"];
  if (typeof authorization === "string" && authorization.length > 0) out.authorization = authorization;
  return out;
}

// Unauthenticated public-appview profile endpoint, used to fetch the signed-in account's avatar for
// the login area. Best-effort UI sugar — a failure just leaves the avatar hidden.
const PROFILE_ENDPOINT = "https://public.api.bsky.app/xrpc/app.bsky.actor.getProfile";

/** Build the unauthenticated profile query URL for a DID or handle. */
export function profileEndpointUrl(actor: string): string {
  const u = new URL(PROFILE_ENDPOINT);
  u.searchParams.set("actor", actor);
  return u.href;
}

/**
 * The gateway's whole-site zip export route, relative to a site's serving root (roadmap item 17).
 *
 * MUST stay in lockstep with the gateway's `EXPORT_PATH` (`packages/server/src/request.ts`), which is
 * the same string with a leading slash. Deliberately duplicated rather than imported: `@szzt/server`
 * is Node-only, and depending on it here would drag node built-ins into the browser bundle. Same stance
 * (and same reason) as `IPFS_HINT_PREFIX` being duplicated between `@szzt/publish` and the gateway.
 */
export const EXPORT_PATH = ".well-known/szzt/export.zip";

/**
 * Build the export-download URL for a site's serving root. `siteRoot` is expected to carry a trailing
 * slash (as the per-site origin URL from `siteUrl` does); a missing one is tolerated. `siteRoot` must be
 * an absolute URL — an empty or relative value throws.
 */
export function exportUrl(siteRoot: string): string {
  // Resolve the export path against the site's absolute origin. Using `new URL` (rather than string
  // concatenation) makes an empty or relative `siteRoot` throw here instead of degrading to a
  // scheme-less path that a browser would resolve to file:/// — fail closed, not open.
  const base = siteRoot.endsWith("/") ? siteRoot : `${siteRoot}/`;
  return new URL(EXPORT_PATH, base).href;
}

/** Extract the avatar URL from a `getProfile` response body, or undefined (defensive, untrusted JSON). */
export function avatarFromProfile(body: unknown): string | undefined {
  if (body === null || typeof body !== "object") return undefined;
  const avatar = (body as { avatar?: unknown }).avatar;
  return typeof avatar === "string" && avatar.length > 0 ? avatar : undefined;
}

/**
 * Sum the byte sizes of a MASL manifest's resources (`resources[path].src.size`), deduping by blob CID
 * (`src.ref.$link`) so the `/` alias entry doesn't double-count the entry document. Returns undefined
 * only when `resources` is not an object; an empty manifest yields 0. Defensive by design — the input
 * is an untrusted resolved record value.
 */
export function totalResourceBytes(resources: unknown): number | undefined {
  if (resources === null || typeof resources !== "object") return undefined;
  const seen = new Set<string>();
  let total = 0;
  for (const entry of Object.values(resources as Record<string, unknown>)) {
    if (entry === null || typeof entry !== "object") continue;
    const src = (entry as { src?: unknown }).src;
    if (src === null || typeof src !== "object") continue;
    const size = (src as { size?: unknown }).size;
    if (typeof size !== "number" || !Number.isFinite(size)) continue;
    const ref = (src as { ref?: unknown }).ref;
    const link = ref !== null && typeof ref === "object" ? (ref as { $link?: unknown }).$link : undefined;
    if (typeof link === "string") {
      if (seen.has(link)) continue;
      seen.add(link);
    }
    total += size;
  }
  return total;
}

/** Format a byte count for display, adapting units (B / KB / MB) so tiny sites don't read "0.00 MB". */
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** Human-readable status line for a given flow state (drives the UI status text). */
export function statusLabel(state: FlowState): string {
  switch (state.step) {
    case "idle":
      return "Drop a file, folder, or .zip to begin.";
    case "building":
      return "Building site…";
    case "ready":
      return `Rendering a sandboxed preview of ${state.built.manifest.name}…`;
    case "previewed":
      return `Preview ready — review it, then publish: ${state.built.manifest.name}`;
    case "publishing":
      return "Publishing to your PDS…";
    case "published":
      return "Published.";
    case "error":
      return `Error while ${state.phase === "build" ? "building" : "publishing"}: ${state.message}`;
  }
}
